import type { PreparationCard } from "~/helpers/aiChat/migrationReview";

import { Box, Button, CircularProgress, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";

import FernieImportQuestions from "~/components/fernie/FernieImportQuestions";
import FernieInspectionTable from "~/components/fernie/FernieInspectionTable";
import LINE_SX from "~/components/fernie/fernieStyles";
import { CaptionText } from "~/components/Typographies";
import migrationReview from "~/helpers/aiChat/migrationReview";
import useLocaleFormat from "~/hooks/useLocaleFormat";

interface FerniePreparationCardProps {
	card: PreparationCard;
	isInspecting: boolean;
	journeyId: string;
	onToggleInspect: () => void;
}

const FerniePreparationCard = ({ card, isInspecting, journeyId, onToggleInspect }: FerniePreparationCardProps) => {
	const { t } = useTranslation("common");
	const { formatNumber } = useLocaleFormat();
	const fileName = card.fileName
		? migrationReview.inertCellText(card.fileName)
		: t("fernieChat.review.preparation.unnamedFile");
	const isPreparing = migrationReview.isPreparingState(card.state);

	return (
		<Box
			component="li"
			sx={{
				backgroundColor: "action.hover",
				borderRadius: "6px",
				display: "flex",
				flexDirection: "column",
				px: 1,
				py: 0.75
			}}
		>
			<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
				<Icon sx={{ color: "text.secondary", fontSize: 14 }}>description</Icon>
				<CaptionText noWrap sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 500, minWidth: 0 }}>
					{fileName}
				</CaptionText>
				{isPreparing && <CircularProgress size={12} />}
				<CaptionText role="status" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
					{t(card.labelKey)}
				</CaptionText>
			</Box>
			<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
				{t("fernieChat.review.preparation.rows", {
					prepared: formatNumber(card.rowsPrepared),
					read: formatNumber(card.rowsRead)
				})}
			</CaptionText>
			{!card.hasImportRun && (
				<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
					{t("fernieChat.review.preparation.notImported")}
				</CaptionText>
			)}
			<FernieImportQuestions jobState={card.state} journeyId={journeyId} sourceId={card.sourceId} />
			{card.rowsPrepared > 0 && (
				<Box>
					<Button
						aria-expanded={isInspecting}
						onClick={onToggleInspect}
						size="small"
						sx={{ fontSize: "0.7rem", px: 0 }}
					>
						{t(isInspecting ? "fernieChat.review.hideInspect" : "fernieChat.review.inspect")}
					</Button>
				</Box>
			)}
			{isInspecting && <FernieInspectionTable fileName={fileName} journeyId={journeyId} sourceId={card.sourceId} />}
		</Box>
	);
};

export default FerniePreparationCard;
